'use client';

import { c as _c } from "react/compiler-runtime";
import { getTeamCostPerTaskBarColor } from '@/domains/pages/ai-spend/components/team-cost-per-task-bars';
import { SEGMENT_SPEND_SCALE_TOOLTIP } from '@/domains/pages/ai-spend/constants';
import type { SegmentSpendSummary } from '@/domains/pages/ai-spend/utils/map-ai-spend-segments';
import { computeSpendScaleAxisBounds } from '@/domains/pages/shared/chart-axis';
import { NebulyPageCard } from '@/domains/pages/shared/nebuly-page-card';
import type { SegmentKind } from '@/domains/pages/shared/segment-kind';
import { segmentKindLabel } from '@/domains/pages/shared/segment-kind';
import { SegmentScaleChart, SegmentScaleChartSkeleton } from '@/domains/pages/shared/segment-scale-chart';
import { capitalizeFirstLetter } from '@/utils/capitalize-first-letter';
import { formatNumber, formatSpend, formatSpendAxisTick } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { useCallback, useMemo } from 'react';
type SegmentSpendScaleCardProps = {
  kind: SegmentKind;
  segments: SegmentSpendSummary[];
  selectedName: string;
  /** Org-wide spend per task, drawn as the reference line the dots are coloured against. */
  orgSpendPerTask: number | null;
  onSelect: (name: string) => void;
  isLoading?: boolean;
  className?: string;
};

/**
 * Axis and dot labels keep about three significant digits, so cheap segments stay apart
 * instead of collapsing into the same "$0.01" as the compact spend format would show.
 */
function formatSpendPerTaskValue(value: number): string {
  if (!Number.isFinite(value) || value === 0) return '$0';
  // Big figures read as "$1.2K" like everywhere else; only the cheap end needs the extra digits.
  if (Math.abs(value) >= 1000) return formatSpend(value);
  const decimals = Math.min(6, Math.max(2, Math.ceil(-Math.log10(Math.abs(value))) + 1));
  const fixed = value.toFixed(decimals);
  const trimmed = decimals > 2 ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
  return `$${trimmed}`;
}
function segmentScaleCardTitle(kind: SegmentKind): string {
  return `${capitalizeFirstLetter(segmentKindLabel(kind))} avg. AI spend per task`;
}
export function SegmentSpendScaleCard(t0) {
  const $ = _c(26);
  const {
    kind,
    segments,
    selectedName,
    orgSpendPerTask,
    onSelect,
    isLoading: t1,
    className
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== segments) {
    t2 = segments.map(_temp);
    $[0] = segments;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scaleSegments = t2;
  let t3;
  if ($[2] !== orgSpendPerTask || $[3] !== segments) {
    const values = segments.map(_temp2).filter(_temp3);
    if (orgSpendPerTask != null) {
      values.push(orgSpendPerTask);
    }
    t3 = computeSpendScaleAxisBounds(values);
    $[2] = orgSpendPerTask;
    $[3] = segments;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const bounds = t3;
  let t4;
  if ($[5] !== orgSpendPerTask) {
    t4 = value_0 => getTeamCostPerTaskBarColor(value_0, orgSpendPerTask);
    $[5] = orgSpendPerTask;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const colorForValue = t4;
  let t5;
  if ($[7] !== segments) {
    t5 = point => {
      const segment_1 = segments.find(candidate => candidate.name === point.name);
      return [{
        label: "AI spend per task",
        value: formatSpend(point.value, {
          precision: "exact"
        })
      }, {
        label: "Total AI spend",
        value: formatSpend(point.weight)
      }, {
        label: "Tasks",
        value: formatNumber(segment_1?.tasks ?? 0)
      }];
    };
    $[7] = segments;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const buildTooltipRows = t5;
  if (!isLoading && scaleSegments.every(_temp4)) {
    return null;
  }
  let t6;
  if ($[9] !== kind) {
    t6 = segmentScaleCardTitle(kind);
    $[9] = kind;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== className) {
    t7 = cn("min-h-0", className);
    $[11] = className;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== bounds || $[14] !== buildTooltipRows || $[15] !== colorForValue || $[16] !== isLoading || $[17] !== onSelect || $[18] !== orgSpendPerTask || $[19] !== scaleSegments || $[20] !== selectedName) {
    t8 = isLoading ? <SegmentScaleChartSkeleton testId="segment-spend-scale-card-skeleton" /> : <SegmentScaleChart segments={scaleSegments} bounds={bounds} selectedName={selectedName} colorForValue={colorForValue} formatValue={formatSpendPerTaskValue} formatTick={tick => formatSpendAxisTick(tick, bounds.stepSize)} buildTooltipRows={buildTooltipRows} metricLabel="AI spend per task" reference={orgSpendPerTask != null ? {
      value: orgSpendPerTask,
      label: "org"
    } : null} onSelect={onSelect} testId="segment-spend-scale-chart" />;
    $[13] = bounds;
    $[14] = buildTooltipRows;
    $[15] = colorForValue;
    $[16] = isLoading;
    $[17] = onSelect;
    $[18] = orgSpendPerTask;
    $[19] = scaleSegments;
    $[20] = selectedName;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <NebulyPageCard title={t6} description={SEGMENT_SPEND_SCALE_TOOLTIP} className={t7} testId="segment-spend-scale-card">{t8}</NebulyPageCard>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp4(segment_2) {
  return segment_2.value == null;
}
function _temp3(value) {
  return value != null;
}
function _temp2(segment_0) {
  return segment_0.spendPerTask;
}
function _temp(segment) {
  return {
    name: segment.name,
    displayName: segment.displayName,
    value: segment.spendPerTask,
    weight: segment.cost
  };
}